import React from 'react';
import Modal from './Modal';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ isOpen, onClose, onAccept }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onAccept={onAccept}
      title="Privacy Policy"
      acceptText="I Accept"
      closeText="Close"
      maxWidth="1000px"
    >
      <div className="privacy-policy-content">
        <p className="last-updated mb-4">Last Updated: January 15, 2025</p>

        {/* Introduction */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">1. Introduction</h3>
          <p className="mb-2">
            Welcome to <strong>Swipey.ai</strong> (the "Service"). This Privacy Policy explains how{' '}
            <strong>InvAI Tech</strong> ("we," "us," or "our") collects, uses, discloses, and protects your information
            when you use our Service available at{' '}
            <a href="https://swipey.ai/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
              swipey.ai
            </a>
            .
          </p>
          <p className="mb-2">
            By using the Service, you agree to the collection and use of information in accordance with this Privacy
            Policy. If you do not agree with this Privacy Policy, please do not use the Service.
          </p>
        </div>

        {/* Information We Collect */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">2. Information We Collect</h3>
          <p className="mb-2">We may collect the following types of information:</p>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">2.1 Personal Information</h4>
            <p className="mb-2">
              When you register for an account or use certain features of the Service, we may collect personal information
              such as:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Age (to verify you are at least 18 years old)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
            </ul>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">2.2 Usage Data</h4>
            <p className="mb-2">
              We automatically collect information about how you interact with the Service, including:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type and operating system</li>
              <li>Pages visited and features used</li>
              <li>Time and date of visits</li>
              <li>Referring website addresses</li>
            </ul>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">2.3 Cookies and Tracking Technologies</h4>
            <p className="mb-2">
              We use cookies and similar tracking technologies to track activity on our Service and store certain
              information. Cookies are small data files stored on your device. You can instruct your browser to refuse all
              cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be
              able to use some portions of our Service.
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">2.4 User Content</h4>
            <p className="mb-2">
              Any content you submit, post, or share through the Service, including messages, images, and other
              user-generated content, may be collected and stored by us.
            </p>
          </div>
        </div>

        {/* How We Use Your Information */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">3. How We Use Your Information</h3>
          <p className="mb-2">We use the collected information for various purposes, including to:</p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Provide, operate, and maintain the Service</li>
            <li>Improve, personalize, and expand the Service</li>
            <li>Understand and analyze how you use the Service</li>
            <li>Develop new products, services, features, and functionality</li>
            <li>
              Communicate with you, including for customer service, updates, and marketing purposes (you may opt out of
              marketing communications)
            </li>
            <li>Process your transactions and manage your subscriptions</li>
            <li>Send you technical notices, updates, security alerts, and support messages</li>
            <li>Detect, prevent, and address technical issues and fraudulent activity</li>
            <li>Comply with legal obligations</li>
          </ul>
        </div>

        {/* Sharing Your Information */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">4. Sharing Your Information</h3>
          <p className="mb-2">We may share your information in the following circumstances:</p>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">4.1 Service Providers</h4>
            <p className="mb-2">
              We may share your information with third-party service providers who perform services on our behalf, such as
              payment processing, data analysis, email delivery, hosting services, and customer service.
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">4.2 Business Transfers</h4>
            <p className="mb-2">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part
              of that transaction. We will notify you of any such change in ownership or control of your personal
              information.
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">4.3 Legal Requirements</h4>
            <p className="mb-2">
              We may disclose your information if required to do so by law or in response to valid requests by public
              authorities (e.g., a court or government agency).
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">4.4 With Your Consent</h4>
            <p className="mb-2">We may share your information with third parties when we have your consent to do so.</p>
          </div>
        </div>

        {/* Data Retention */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">5. Data Retention</h3>
          <p className="mb-2">
            We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this
            Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your
            information, we will securely delete or anonymize it.
          </p>
        </div>

        {/* Data Security */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">6. Data Security</h3>
          <p className="mb-2">
            We take the security of your personal information seriously and implement appropriate technical and
            organizational measures to protect it from unauthorized access, disclosure, alteration, or destruction.
            However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot
            guarantee absolute security.
          </p>
        </div>

        {/* Your Rights and Choices */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">7. Your Rights and Choices</h3>
          <p className="mb-2">Depending on your location, you may have the following rights regarding your personal information:</p>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">7.1 Access and Correction</h4>
            <p className="mb-2">
              You have the right to access and update your personal information through your account settings or by
              contacting us.
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">7.2 Data Deletion</h4>
            <p className="mb-2">
              You may request that we delete your personal information, subject to certain exceptions required by law. To
              request deletion, please contact us at{' '}
              <a href="mailto:admin@invai.tech" className="text-blue-500 underline">
                admin@invai.tech
              </a>
              .
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">7.3 Opt-Out of Marketing</h4>
            <p className="mb-2">
              You may opt out of receiving marketing communications from us by following the unsubscribe instructions in
              those communications or by contacting us.
            </p>
          </div>

          <div className="subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">7.4 Do Not Track</h4>
            <p className="mb-2">
              Some browsers have a "Do Not Track" feature. We do not currently respond to "Do Not Track" signals.
            </p>
          </div>
        </div>

        {/* Third-Party Links */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">8. Third-Party Links</h3>
          <p className="mb-2">
            The Service may contain links to third-party websites or services that are not operated by us. We are not
            responsible for the privacy practices of these third parties. We encourage you to review the privacy policies
            of any third-party sites you visit.
          </p>
        </div>

        {/* Children's Privacy */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">9. Children's Privacy</h3>
          <p className="mb-2">
            The Service is not intended for individuals under the age of 18. We do not knowingly collect personal
            information from anyone under 18. If we become aware that we have collected personal information from a person
            under 18, we will take steps to delete that information.
          </p>
        </div>

        {/* International Data Transfers */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">10. International Data Transfers</h3>
          <p className="mb-2">
            Your information may be transferred to and maintained on computers located outside of your country or other
            governmental jurisdiction where data protection laws may differ. By using the Service, you consent to the
            transfer of your information to such locations.
          </p>
        </div>

        {/* Changes to This Privacy Policy */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">11. Changes to This Privacy Policy</h3>
          <p className="mb-2">
            We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last
            Updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for
            any changes. Your continued use of the Service after any modifications constitutes your acceptance of the
            updated Privacy Policy.
          </p>
        </div>

        {/* Contact Us */}
        <div className="policy-section mb-6">
          <h3 className="font-semibold text-lg mb-2">12. Contact Us</h3>
          <p className="mb-2">If you have any questions about this Privacy Policy, please contact us at:</p>
          <p className="mb-2">
            <strong>Email:</strong>{' '}
            <a href="mailto:admin@invai.tech" className="text-blue-500 underline">
              admin@invai.tech
            </a>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyPolicy;
